import { ProductVatConfigData } from '@/types/product-vat-config.types';

export enum PriceUnit {
  PER_DAY = 'PER_DAY',
  PER_HOUR = 'PER_HOUR',
}

export type AddonAccessHandoverMode = 'NONE' | 'KEY_BOX' | 'ACCESS_CODE';
export type AddonTransactionPresentationMode = 'RESERVATION' | 'PAYMENT_ONLY';

export interface AddonAvailabilityDateRange {
  start_date: string;
  end_date: string;
}

export interface IAddonData {
  id: string;
  harbor_id: string;
  product_vat_config_id?: string | null;
  name: string;
  description?: string;
  price?: number;
  price_unit: PriceUnit;
  is_available: boolean;
  is_standalone: boolean;
  quantity?: number;
  transaction_presentation_mode?: AddonTransactionPresentationMode | null;
  access_handover_mode?: AddonAccessHandoverMode;
  /** Canonical / DeepL source language (ISO code, e.g. en) */
  language?: string;
  translations?: Record<
    string,
    {
      name?: string;
      description?: string;
      key_box_pickup_instructions?: string;
      key_box_return_instructions?: string;
    }
  > | null;
  key_box_code?: string;
  key_box_pickup_instructions?: string;
  key_box_return_instructions?: string;
  /** ISO language code → pickup / return text for key-box handover */
  key_box_instruction_translations?: Record<
    string,
    { pickup?: string; return?: string }
  >;
  condition?: string;
  conditionUpdatedByUser?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email: string;
  };
  condition_updated_at?: string;
  harbor?: {
    id: string;
    name: string;
  };
  start_time: string;
  end_time: string;
  availability_date_ranges?: AddonAvailabilityDateRange[] | null;
}

export interface IAddonById {
  data: IAddonData;
}

export interface AddonResponse {
  status: boolean;
  message: string;
  data: {
    id: string;
    harbor_id: string;
    name: string;
    description?: string;
    price?: number;
    price_unit: 'PER_DAY' | 'PER_WEEK' | 'PER_MONTH' | 'PER_YEAR';
    is_available: boolean;
    is_standalone: boolean;
    quantity?: number;
    product_vat_config_id?: string | null;
    transaction_presentation_mode?: AddonTransactionPresentationMode | null;
    access_handover_mode?: AddonAccessHandoverMode;
    language?: string | null;
    translations?: Record<
      string,
      {
        name?: string;
        description?: string;
        key_box_pickup_instructions?: string;
        key_box_return_instructions?: string;
      }
    > | null;
    key_box_code?: string | null;
    key_box_pickup_instructions?: string | null;
    key_box_return_instructions?: string | null;
    key_box_instruction_translations?: Record<
      string,
      { pickup?: string; return?: string }
    > | null;
    condition?: string;
    conditionUpdatedByUser?: {
      id: string;
      first_name?: string;
      last_name?: string;
      email: string;
    };
    condition_updated_at?: string;
    harbor?: {
      id: string;
      name: string;
      location?: string;
    };
    start_time?: string;
    end_time?: string;
    availability_date_ranges?: AddonAvailabilityDateRange[] | null;
    status?: number;
    created_at?: string;
    updated_at?: string;
  };
}

export interface AddonListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  harbor_id?: string;
  is_available?: boolean;
}

export interface UpdateAvailablePayload {
  addonId: string;
  is_available: boolean;
}

/** Read-only VAT regime option for add-on editors (GET /addons/vat-regimes). */
export type AddonVatRegime = Pick<
  ProductVatConfigData,
  'id' | 'default_vat_rate' | 'active_from' | 'active_to'
>;

export interface AddonVatRegimeListResponse {
  status?: boolean;
  data: AddonVatRegime[];
}
